import experiment from 'experiment';
import * as util from '#packages/util';
import {
  getRefComponentRootChild,
  getRefComponentStageData,
} from '#packages/documentServices';
import type {
  ComponentEditorMetaDataDefinition,
  ComponentEditorMetaDataValueResolver,
} from '../types';

import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef, ComponentStageBehavior } from 'types/documentServices';

const focusable: ComponentEditorMetaDataDefinition['focusable'] = (
  editorAPI,
  compRef,
) => {
  const mainChildRef = getRefComponentRootChild(editorAPI.dsRead, compRef);

  return !!mainChildRef && editorAPI.components.is.focusable(mainChildRef);
};

const forbiddenResizeDueToStageData: ComponentEditorMetaDataValueResolver<
  boolean
> = (editorAPI, compRef) => {
  const refComponentStageData = getRefComponentStageData(
    editorAPI.dsRead,
    compRef,
  );
  return refComponentStageData?.behavior?.resizable === false;
};

const resizable: ComponentEditorMetaDataDefinition['resizable'] = (
  editorAPI,
  compRef,
) => {
  const { dsRead } = editorAPI;
  return (
    !editorAPI.platform.widgetPlugins.isWidgetPluginComponent(compRef) &&
    util.appStudioUtils.isResponsiveBlocksWidget({ dsRead }, compRef) &&
    !forbiddenResizeDueToStageData(editorAPI, compRef)
  );
};

function pinnable(editorAPI: EditorAPI, compRef: CompRef) {
  return !editorAPI.platform.widgetPlugins.isWidgetPluginComponent(compRef);
}

function duplicatable(editorAPI: EditorAPI, compRef: CompRef) {
  return !editorAPI.platform.widgetPlugins.isWidgetPluginComponent(compRef);
}

const containable: ComponentEditorMetaDataValueResolver<boolean> = (
  editorAPI: EditorAPI,
  compRef: CompRef,
  potentialParent?: CompRef,
) => {
  if (!potentialParent) {
    return;
  }

  const isRepeaterContainer =
    editorAPI.components.is.repeatedComponent(potentialParent);

  if (isRepeaterContainer) {
    // TODO: use isRepeatable meta data here when it is production ready
    return experiment.isOpen('se_widgetInRepeater');
  }

  const isWidgetPluginComponent =
    editorAPI.platform.widgetPlugins.isWidgetPluginComponent(compRef);

  return !isWidgetPluginComponent;
};

const canContainByStructure: ComponentEditorMetaDataValueResolver<boolean> = (
  editorAPI: EditorAPI,
  potentialParent: CompRef,
) => containable(editorAPI, null, potentialParent);

function getDelegatedRefComponentBehaviorMetadataHandlers(): Partial<ComponentEditorMetaDataDefinition> {
  const defaultMetaDataValues: Partial<ComponentStageBehavior> = {
    removable: true,
  };

  const defaultMetaDataValueResolvers: {
    [key in keyof ComponentStageBehavior]: ComponentEditorMetaDataValueResolver<
      ComponentStageBehavior[key]
    >;
  } = {
    pinnable,
    duplicatable,
  };

  const behaviourMetadataKeys = [
    ...Object.keys(defaultMetaDataValues),
    ...Object.keys(defaultMetaDataValueResolvers),
  ] as (keyof ComponentStageBehavior)[];

  const behaviourMetadataHandlers = {} as {
    [key in keyof ComponentStageBehavior]: ComponentEditorMetaDataValueResolver<
      ComponentStageBehavior[key]
    >;
  };

  for (const handlerName of behaviourMetadataKeys) {
    behaviourMetadataHandlers[handlerName] = (
      editorAPI,
      compRef,
      ...extraArgs
    ) => {
      const controllerStageData = getRefComponentStageData(
        editorAPI.dsRead,
        compRef,
      );

      if (controllerStageData?.behavior?.[handlerName] !== undefined) {
        return controllerStageData?.behavior?.[handlerName];
      }

      if (defaultMetaDataValues[handlerName] !== undefined) {
        return defaultMetaDataValues[handlerName];
      }

      if (defaultMetaDataValueResolvers[handlerName] !== undefined) {
        return defaultMetaDataValueResolvers[handlerName](
          editorAPI,
          compRef,
          ...extraArgs,
        ) as any;
      }
    };
  }

  return behaviourMetadataHandlers as Partial<ComponentEditorMetaDataDefinition>;
}

const refComponentMetaData: ComponentEditorMetaDataDefinition = {
  isDirectChildrenSelectable: false,
  selectedBeforeDescendants: true,
  delegateControlsToFirstChild: {
    applyToAll: true,
  },
  applyMetaDataToFirstChild: {
    skipInLayersPanel: true,
    isAllowedAddToSavedComponent: false,
  },
  selectable: true,
  isAllowedAddToSavedComponent: false,
  focusable,
  resizable,
  horizontallyResizable: resizable,
  verticallyResizable: resizable,
  stretchable: resizable,
  containable,
  canContainByStructure,
  ...getDelegatedRefComponentBehaviorMetadataHandlers(),
};

export default refComponentMetaData;
